import { Link } from 'react-router-dom';
import { SEOPageLayout } from '@/components/seo/SEOPageLayout';

const faq = [
  { q: 'How do I thank my parents in a meaningful way?', a: 'Go beyond "thank you." Name specific sacrifices, moments, or qualities. Tell them how their actions shaped who you are today. Specificity is what transforms gratitude from polite to profound.' },
  { q: 'Should I thank both parents in one message or separately?', a: 'Separate messages allow you to be more personal and specific. Each parent shaped you differently, and individual messages honor those unique contributions.' },
  { q: 'What if my relationship with my parents is complicated?', a: 'Gratitude doesn\'t require a perfect relationship. You can acknowledge specific positive things while honoring the complexity. JacKey can help you navigate this with sensitivity.' },
  { q: 'Is there a good time to send a gratitude message?', a: 'The best time is when it\'s unexpected — not on a holiday. Spontaneous gratitude carries more emotional weight because it shows genuine thinking, not obligation.' },
];

const related = [
  { label: 'Heartfelt Message to Mom', href: '/heartfelt-message-to-mom' },
  { label: 'Heartfelt Message to Dad', href: '/heartfelt-message-to-dad' },
  { label: 'Thank You Message Generator', href: '/tools/thank-you-message-generator' },
  { label: 'Living Tribute Ideas', href: '/living-tribute-ideas' },
];

export default function ThankYouMessageForParents() {
  return (
    <SEOPageLayout
      title="Thank You Message for Parents — Gratitude Examples"
      metaDescription="Write a meaningful thank you message for your parents. Real examples, emotional guidance, and a free message creator to express gratitude while they can still hear it."
      h1="Thank You Messages for Parents — Gratitude They Deserve to Hear"
      subtitle="Your parents gave you life. Here's how to give them the words they've earned."
      faq={faq}
      relatedPages={related}
    >
      <h2>The Power of Saying Thank You to Your Parents</h2>
      <p>
        There's a paradox in families: the people who sacrifice the most for us are often the ones we thank the least. We assume they know. We assume it's obvious. But studies on gratitude consistently show that <strong>expressed thanks — specific, personal, and heartfelt — has a measurable impact</strong> on both the person giving thanks and the person receiving it.
      </p>
      <p>
        Your parents don't need another gift card. They need to hear, in your words, that their sacrifices mattered.
      </p>

      <h2>Thank You Message Examples for Parents</h2>

      <h3>For Everything They Sacrificed</h3>
      <blockquote>
        "Mom and Dad, I'm only now beginning to understand what you gave up so I could have more. The vacations you skipped, the things you went without, the exhaustion you never complained about — I see it all now. Thank you for choosing us, every single time."
      </blockquote>

      <h3>For Their Unconditional Support</h3>
      <blockquote>
        "Thank you for never making your love conditional. When I failed, you didn't shame me — you helped me get back up. When I succeeded, you celebrated like it was your victory too. That kind of support shaped everything I am."
      </blockquote>

      <h3>For the Small Things</h3>
      <blockquote>
        "Thank you for the lunches you packed, the stories you read, the nightlights you kept on because I was scared of the dark. Thank you for a childhood where I felt safe enough to become who I am."
      </blockquote>

      <h3>For Immigrant Parents</h3>
      <blockquote>
        "You left everything you knew so I could have what you never did. You learned a new language, worked jobs below your talent, and swallowed your pride — all for us. I will never forget where I came from, because you made sure I always knew."
      </blockquote>

      <h2>How to Write Your Own Thank You Message</h2>
      <ol>
        <li><strong>Think of one specific sacrifice.</strong> Not "everything" — one real thing.</li>
        <li><strong>Describe how it affected you.</strong> What did it teach you? How did it shape your life?</li>
        <li><strong>Say thank you directly.</strong> Use those exact words.</li>
        <li><strong>Tell them you see them.</strong> Parents often feel invisible in their sacrifices.</li>
      </ol>
      <p>
        Need help getting started? Try our free <Link to="/tools/thank-you-message-generator">Thank You Message Generator</Link> or <Link to="/signup">create a full Living Announcement</Link> with photos and beautiful templates.
      </p>

      <h2>Make It More Than Words</h2>
      <p>
        <Link to="/signup">Say It NOW</Link> transforms your thank-you into a <strong>digital tribute</strong> — a beautiful, lasting keepsake. Choose a premium template, add family photos, and share it privately. Your gratitude becomes a part of your family's legacy through the <Link to="/faq">Living Tree</Link>.
      </p>
    </SEOPageLayout>
  );
}
